import { useEffect, useRef, useState } from "react";
import { Outlet } from "react-router-dom";
import TitleBar from "@/components/shell/TitleBar";
import Sidebar from "@/components/shell/Sidebar";
import StatusStrip from "@/components/shell/StatusStrip";
import Ni2Palette from "@/components/system/Ni2Palette";
import InspectorSidebar from "@/components/system/InspectorSidebar";
import { SHELL } from "@/constants/testIds";
import { APPEARANCE_MODES, useAppearance } from "@/context/AppearanceContext";

const INSPECTOR_WIDTH = 360;

export default function AppShell() {
  const atmosphereRef = useRef(null);
  const { mode } = useAppearance();
  const [ni2Open, setNi2Open] = useState(false);
  const [inspectorOpen, setInspectorOpen] = useState(false);

  // Faint parallax on the atmospheric background
  useEffect(() => {
    let raf = 0;
    const onMove = (e) => {
      const el = atmosphereRef.current;
      if (!el) return;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const x = (e.clientX / window.innerWidth - 0.5) * 12;
        const y = (e.clientY / window.innerHeight - 0.5) * 8;
        el.style.transform = `scale(1.04) translate3d(${-x}px, ${-y}px, 0)`;
      });
    };
    window.addEventListener("mousemove", onMove, { passive: true });
    return () => {
      window.removeEventListener("mousemove", onMove);
      cancelAnimationFrame(raf);
    };
  }, []);

  // ESC closes the Ni² palette or Inspector
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (ni2Open) setNi2Open(false);
        else if (inspectorOpen) setInspectorOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [ni2Open, inspectorOpen]);

  return (
    <div className="relative h-screen w-screen overflow-hidden text-[hsl(var(--nn-ink))]">
      {/* Atmospheric layers — one per mode, crossfaded via opacity */}
      <div ref={atmosphereRef} className="nn-atmosphere-stack" aria-hidden="true">
        {APPEARANCE_MODES.map((m) => (
          <div
            key={m.id}
            className={`nn-atmosphere-layer nn-mode-${m.id}`}
            style={{ opacity: mode === m.id ? 1 : 0 }}
          />
        ))}
      </div>
      <div className="nn-grain" aria-hidden="true" />

      <div className="relative z-10 flex h-full w-full flex-col">
        <TitleBar
          ni2Open={ni2Open}
          setNi2Open={setNi2Open}
          inspectorOpen={inspectorOpen}
          setInspectorOpen={setInspectorOpen}
        />

        <div className="flex flex-1 min-h-0 gap-5 px-5 pb-3 pt-2">
          <Sidebar />

          {/* Workspace column — resizes when inspector opens */}
          <main
            data-testid={SHELL.workspace}
            className="nn-scroll relative min-w-0 flex-1 overflow-y-auto overflow-x-hidden pr-1"
            style={{
              transition:
                "flex-basis 260ms cubic-bezier(0.32, 0.72, 0.24, 1)",
            }}
          >
            <div className="mx-auto max-w-[1240px] pt-4 pb-10">
              <Outlet />
            </div>
          </main>

          {/* Inspector column — inline, width animates from 0 → INSPECTOR_WIDTH */}
          <div
            aria-hidden={!inspectorOpen}
            className="relative h-full shrink-0 overflow-hidden"
            style={{
              width: inspectorOpen ? INSPECTOR_WIDTH : 0,
              transition:
                "width 260ms cubic-bezier(0.32, 0.72, 0.24, 1)",
            }}
          >
            {/* Keep the panel mounted while open so animation is clean */}
            <div
              style={{
                width: INSPECTOR_WIDTH,
                height: "100%",
                transform: inspectorOpen ? "translateX(0)" : "translateX(24px)",
                opacity: inspectorOpen ? 1 : 0,
                transition:
                  "transform 260ms cubic-bezier(0.32, 0.72, 0.24, 1), opacity 220ms ease",
                pointerEvents: inspectorOpen ? "auto" : "none",
              }}
            >
              <InspectorSidebar onClose={() => setInspectorOpen(false)} />
            </div>
          </div>
        </div>

        <StatusStrip />
      </div>

      {/* Ni² palette — floating, non-layout affecting */}
      {ni2Open && <Ni2Palette onClose={() => setNi2Open(false)} />}
    </div>
  );
}
